/**
 * @praesto/core/usage — sharded rate-limit counters.
 *
 * `current`/`assertWithinLimit` sum a counter's shard subcollection
 * (/tenants/{tid}/usage-counters/{counter}/shards/*) — sharding avoids the
 * single-doc hotspot. `increment` is server-only: firestore.rules forbid
 * client writes to usage-counters, so it goes through a Cloud Function
 * (wired in Fase 5). Reads are the Milestone-1 critical path (the Procedure
 * assistant calls assertWithinLimit before every AI query).
 *
 * Contract: packages/contracts/src/core-api.ts (UsageService).
 */
import type { CounterName, UsageService } from "@praesto/contracts";
import { collection, getDocs } from "firebase/firestore";
import type { RuntimeContext } from "../internal/context";
import { tenantPath } from "../internal/paths";

export class RateLimitExceededError extends Error {
  constructor(counter: string, limit: number, current: number) {
    super(`Rate limit exceeded for "${counter}": ${current} >= ${limit}`);
    this.name = "RateLimitExceededError";
  }
}

export function createUsageService(ctx: RuntimeContext): UsageService {
  const { db } = ctx.firebase;

  async function current(counter: CounterName): Promise<number> {
    const snap = await getDocs(
      collection(db, tenantPath(ctx.tenantId, "usage-counters", counter, "shards")),
    );
    return snap.docs.reduce((sum, d) => {
      const c = (d.data() as { count?: number }).count;
      return sum + (typeof c === "number" ? c : 0);
    }, 0);
  }

  return {
    current,
    async assertWithinLimit(counter: CounterName, limit: number): Promise<void> {
      const value = await current(counter);
      if (value >= limit) {
        throw new RateLimitExceededError(counter, limit, value);
      }
    },
    increment(): Promise<number> {
      // Client writes to usage-counters are denied by firestore.rules.
      return Promise.reject(
        new Error("[usage] increment is server-only; wired via Cloud Function in Fase 5"),
      );
    },
  };
}
